import { Emoji, EmojiSet, Icon, IconSet } from '@postscript/components';
import { APP_LAYOUT_CSS_SELECTORS } from 'constants/constants';
import React from 'react';
import styled from 'styled-components';
import MediaQueries from 'utils/mediaQueries';

interface NavMenuButtonInterface
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  easterEggEmoji?: boolean;
  isSidebarNavigationOpen: boolean;
}

const NavMenuIcon = styled(Icon)`
  transition: color var(--hover-transition);
`;

const StyledNavMenuButton = styled.button<NavMenuButtonInterface>`
  background: transparent;
  display: none;
  align-items: center;
  border: none;
  height: 48px;
  width: 48px;
  padding: var(--spacing-2);
  cursor: pointer;
  transition: color var(--hover-transition),
    transform var(--nav-menu-transition-speed);

  position: absolute;
  z-index: 1000;
  top: 0;
  left: 0;

  /* DEFAULT */

  ${NavMenuIcon} {
    color: var(--black);
  }

  [data-theme='dark'] & {
    ${NavMenuIcon} {
      color: var(--white);
    }
  }

  /* HOVER + FOCUS */

  &:hover,
  &:focus-visible {
    ${NavMenuIcon} {
      color: var(--highlight-color);
    }
  }

  /* ACTIVE */

  &:active {
    ${NavMenuIcon} {
      color: var(--link-color-hover);
    }
  }

  /* browser patches */

  appearance: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  &::-moz-focus-inner {
    border: 0;
    padding: 0;
    margin-top: -2px;
    margin-bottom: -2px;
  }

  /* focus states */

  &:focus,
  &:focus-visible {
    outline: 0;
  }

  ${MediaQueries.desktopOnly} {
    ${APP_LAYOUT_CSS_SELECTORS.FULLSCREEN_EDITOR} & {
      display: flex;
      top: 0;
      left: 0;
      transform: translate(0, 0);
    }

    ${APP_LAYOUT_CSS_SELECTORS.FULLSCREEN_EDITOR}${APP_LAYOUT_CSS_SELECTORS.SIDEBAR_OPEN} & {
      transform: translate(261px, 51px);
    }
  }

  ${MediaQueries.tabletOnly} {
    display: flex;

    ${APP_LAYOUT_CSS_SELECTORS.SIDEBAR_OPEN} & {
      transform: translate(-63px, 39px);
    }
  }

  ${MediaQueries.phoneOnly} {
    display: flex;

    ${APP_LAYOUT_CSS_SELECTORS.SIDEBAR_OPEN} & {
      transform: translate(-63px, 15px);
    }
  }
`;

const NavMenuButton = ({
  easterEggEmoji = false,
  isSidebarNavigationOpen,
  onClick,
  ...props
}: NavMenuButtonInterface): JSX.Element => (
  <StyledNavMenuButton
    aria-label="Toggle Navigation Menu"
    role="button"
    isSidebarNavigationOpen={isSidebarNavigationOpen}
    onClick={onClick}
    tabIndex={0}
    type="button"
    {...props}
  >
    {easterEggEmoji && !isSidebarNavigationOpen ? (
      <Emoji component={EmojiSet.Hamburger} size={24} />
    ) : (
      <NavMenuIcon
        color="currentColor"
        component={isSidebarNavigationOpen ? IconSet.Close : IconSet.Menu}
        size={24}
      />
    )}
  </StyledNavMenuButton>
);

export default NavMenuButton;
